import React, { useEffect, useState } from "react";
import axiosInstance from "../../services/auth";
import { toast } from "react-toastify";
import "./Logistics.css";

const TransferForm = ({ companyId, transfer, onClose, onSuccess }) => {
  const authData =
    JSON.parse(localStorage.getItem("authData")) ||
    JSON.parse(sessionStorage.getItem("authData"));

  const currentUserId = authData?._id;

  const [loading, setLoading] = useState(false);

  const [stocks, setStocks] = useState([]);
  const [stockSearches, setStockSearches] = useState({});
  const [showStockDropdown, setShowStockDropdown] = useState({});

  const [formData, setFormData] = useState({
    date: "",
    remarks: "",
    company: companyId,
    createdBy: currentUserId,
    items: [
      {
        stock: "",
        quantity: "",
      },
    ],
  });

  useEffect(() => {
    const fetchStocks = async () => {
      try {
        const response = await axiosInstance.get(
          `/logistics/stock/${companyId}`,
        );
        setStocks(response.data.stocks || []);
      } catch (error) {
        toast.error("Error fetching stocks");
      }
    };

    if (companyId) fetchStocks();
  }, [companyId]);

  //-------------------------------------------------------
  // Load Edit Data
  //-------------------------------------------------------

  useEffect(() => {
    if (transfer) {
      setFormData({
        date: transfer.date ? transfer.date.substring(0, 10) : "",
        remarks: transfer.remarks || "",
        company: transfer.company,
        createdBy: currentUserId,

        items: transfer.items.map((item) => ({
          stock: item.stock._id || item.stock,
          quantity: item.quantity,
        })),
      });
    } else {
      setFormData((prev) => ({
        ...prev,
        date: new Date().toISOString().substring(0, 10),
      }));
    }

    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  //-------------------------------------------------------
  // Close Modal
  //-------------------------------------------------------

  const handleOutsideClick = (e) => {
    if (e.target.classList.contains("modal")) {
      onClose();
    }
  };

  //-------------------------------------------------------
  // Change Header Fields
  //-------------------------------------------------------

  const handleHeaderChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  //-------------------------------------------------------
  // Change Item
  //-------------------------------------------------------

  const handleItemChange = (index, field, value) => {
    const items = [...formData.items];

    items[index][field] = value;

    // Prevent duplicate products

    if (field === "stock") {
      const duplicate = items.findIndex(
        (x, i) => x.stock === value && i !== index,
      );

      if (duplicate !== -1) {
        toast.error("Product already selected");
        return;
      }
    }

    // Validate Quantity

    if (field === "quantity") {
      const stock = stocks.find((s) => s._id === items[index].stock);

      if (stock) {
        if (Number(value) > Number(stock.stockAQuantity)) {
          toast.error(`Available in Stock A : ${stock.stockAQuantity}`);

          items[index].quantity = "";

          setFormData((prev) => ({
            ...prev,
            items,
          }));

          return;
        }
      }
    }

    setFormData((prev) => ({
      ...prev,
      items,
    }));
  };

  //-------------------------------------------------------
  // Add Item
  //-------------------------------------------------------

  const addItem = () => {
    setFormData((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        {
          stock: "",
          quantity: "",
        },
      ],
    }));
  };

  //-------------------------------------------------------
  // Remove Item
  //-------------------------------------------------------

  const removeItem = (index) => {
    if (formData.items.length === 1) {
      toast.error("At least one product required");
      return;
    }

    const items = [...formData.items];

    items.splice(index, 1);

    setFormData((prev) => ({
      ...prev,
      items,
    }));
  };

  //-------------------------------------------------------
  // Get Available Qty
  //-------------------------------------------------------

  const getAvailableQty = (stockId) => {
    const stock = stocks.find((s) => s._id === stockId);

    if (!stock) return 0;

    return stock.stockAQuantity;
  };

  //-------------------------------------------------------
  // Validation
  //-------------------------------------------------------

  const validateForm = () => {
    if (!formData.date) {
      toast.error("Transfer date required");
      return false;
    }

    if (formData.items.length === 0) {
      toast.error("Add at least one product");
      return false;
    }

    for (let item of formData.items) {
      if (!item.stock) {
        toast.error("Please select product");
        return false;
      }

      if (!item.quantity || Number(item.quantity) <= 0) {
        toast.error("Invalid quantity");
        return false;
      }

      const stock = stocks.find((s) => s._id === item.stock);

      if (stock && Number(item.quantity) > Number(stock.stockAQuantity)) {
        toast.error(`${stock.name} has only ${stock.stockAQuantity} available`);

        return false;
      }
    }

    return true;
  }; //-------------------------------------------------------
  // Submit
  //-------------------------------------------------------

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);

    try {
      const payload = {
        company: companyId,
        date: formData.date,
        remarks: formData.remarks,
        createdBy: currentUserId,
        items: formData.items.map((item) => ({
          stock: item.stock,
          quantity: Number(item.quantity),
        })),
      };

      if (transfer) {
        await axiosInstance.patch(
          `/logistics/transfer/${transfer._id}`,
          payload,
        );

        toast.success("Transfer updated successfully");
      } else {
        await axiosInstance.post("/logistics/transfer", payload);

        toast.success("Transfer completed successfully");
      }

      if (onSuccess) onSuccess();

      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save transfer");
    } finally {
      setLoading(false);
    }
  };

  //-------------------------------------------------------
  // JSX
  //-------------------------------------------------------

  return (
    <div
      className="modal show d-block"
      tabIndex="-1"
      onClick={handleOutsideClick}
      style={{
        display: "block",
        backgroundColor: "rgba(0,0,0,.5)",
      }}
    >
      <div className="modal-dialog modal-xl modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h4 className="modal-title">
              {transfer ? "Edit Stock Transfer" : "New Stock Transfer"}
            </h4>

            <button className="btn-close" onClick={onClose} />
          </div>

          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              <div className="row">
                <div className="col-md-4 mb-3">
                  <label className="form-label">Transfer Date</label>

                  <input
                    type="date"
                    name="date"
                    className="form-control"
                    value={formData.date}
                    onChange={handleHeaderChange}
                  />
                </div>

                <div className="col-md-8 mb-3">
                  <label className="form-label">Remarks</label>

                  <input
                    type="text"
                    name="remarks"
                    className="form-control"
                    value={formData.remarks}
                    onChange={handleHeaderChange}
                  />
                </div>
              </div>

              <hr />

              <table className="table table-bordered">
                <thead>
                  <tr>
                    <th width="45%">Product</th>

                    <th width="20%">Available A</th>

                    <th width="20%">Transfer Qty</th>

                    <th width="15%">Action</th>
                  </tr>
                </thead>

                <tbody>
                  {formData.items.map((item, index) => (
                    <tr key={index}>
                      <td>
                        <div className="position-relative">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Type stock number or name"
                            value={
                              stockSearches[index] ||
                              (item.stock
                                ? (() => {
                                    const s = stocks.find(
                                      (st) => st._id === item.stock,
                                    );
                                    return s
                                      ? `${s.stockNo} - ${s.name}`
                                      : "";
                                  })()
                                : "")
                            }
                            onChange={(e) => {
                              setStockSearches((prev) => ({
                                ...prev,
                                [index]: e.target.value,
                              }));
                              handleItemChange(index, "stock", "");
                            }}
                            onFocus={() => {
                              setShowStockDropdown((prev) => {
                                const next = {};
                                next[index] = true;
                                return next;
                              });
                            }}
                            onBlur={() => {
                              setTimeout(() => {
                                setShowStockDropdown((prev) => ({
                                  ...prev,
                                  [index]: false,
                                }));
                              }, 150);
                            }}
                            required
                          />
                          {showStockDropdown[index] && (
                            <ul
                              className="list-group position-absolute"
                              style={{
                                zIndex: 1200,
                                width: "100%",
                                maxHeight: 200,
                                overflowY: "auto",
                              }}
                            >
                              {stocks
                                .filter(
                                  (stock) =>
                                    (stock.name || "")
                                      .toLowerCase()
                                      .includes(
                                        (stockSearches[index] || "")
                                          .toLowerCase(),
                                      ) ||
                                    (stock.stockNo
                                      ? stock.stockNo.toString()
                                      : ""
                                    ).includes(
                                      stockSearches[index] || "",
                                    ),
                                )
                                .map((stock) => (
                                  <li
                                    key={stock._id}
                                    className="list-group-item list-group-item-action"
                                    style={{ cursor: "pointer" }}
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => {
                                      handleItemChange(
                                        index,
                                        "stock",
                                        stock._id,
                                      );
                                      setStockSearches((prev) => ({
                                        ...prev,
                                        [index]: "",
                                      }));
                                      setShowStockDropdown((prev) => ({
                                        ...prev,
                                        [index]: false,
                                      }));
                                    }}
                                  >
                                    {stock.stockNo} - {stock.name}
                                  </li>
                                ))}
                              {stocks.filter(
                                (stock) =>
                                  (stock.name || "")
                                    .toLowerCase()
                                    .includes(
                                      (stockSearches[index] || "")
                                        .toLowerCase(),
                                    ) ||
                                  (stock.stockNo
                                    ? stock.stockNo.toString()
                                    : ""
                                  ).includes(
                                    stockSearches[index] || "",
                                  ),
                              ).length === 0 && (
                                <li className="list-group-item list-group-item-action">
                                  No results found
                                </li>
                              )}
                            </ul>
                          )}
                        </div>
                      </td>

                      <td>
                        <input
                          className="form-control"
                          readOnly
                          value={getAvailableQty(item.stock)}
                        />
                      </td>

                      <td>
                        <input
                          type="number"
                          min="1"
                          className="form-control"
                          value={item.quantity}
                          onChange={(e) =>
                            handleItemChange(index, "quantity", e.target.value)
                          }
                        />
                      </td>

                      <td>
                        <button
                          type="button"
                          className="btn btn-danger btn-sm"
                          onClick={() => removeItem(index)}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <button
                type="button"
                className="btn btn-success"
                onClick={addItem}
              >
                + Add Product
              </button>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
              >
                Cancel
              </button>

              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
              >
                {loading
                  ? "Saving..."
                  : transfer
                    ? "Update Transfer"
                    : "Transfer Stock"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default TransferForm;
